import React, { useState, useEffect, useCallback } from 'react';
import { useGuest } from '../context/GuestContext';
import '../styles/GuestPairingAdmin.css';

const GuestPairingAdmin = ({ onClose }) => {
  const { guestList, updatePartner } = useGuest();
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('');

  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  const paired = guestList.filter((g) => g.partner);
  const unpaired = guestList.filter((g) => !g.partner);

  const uniquePairs = [];
  const seen = new Set();
  paired.forEach((g) => {
    const key = [g.firstName, g.partner].sort().join('|');
    if (!seen.has(key)) {
      seen.add(key);
      const partnerGuest = guestList.find((p) => p.firstName === g.partner);
      uniquePairs.push({ a: g, b: partnerGuest });
    }
  });

  const handleSelect = (guest) => {
    if (guest.partner) return;
    if (!selected) {
      setSelected(guest);
    } else if (selected.firstName === guest.firstName && selected.surname === guest.surname) {
      setSelected(null);
    } else {
      updatePartner(selected.firstName, guest.firstName);
      updatePartner(guest.firstName, selected.firstName);
      setSelected(null);
    }
  };

  const handleUnpair = (guest) => {
    const partnerName = guest.partner;
    updatePartner(guest.firstName, null);
    updatePartner(partnerName, null);
  };

  const filtered = unpaired.filter((g) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      g.firstName.toLowerCase().includes(q) ||
      g.surname.toLowerCase().includes(q) ||
      (g.displayName && g.displayName.toLowerCase().includes(q))
    );
  });

  return (
    <div className="pairing-overlay" onClick={onClose}>
      <div className="pairing-panel" onClick={(e) => e.stopPropagation()}>
        <div className="pairing-header">
          <h2>Guest Pairing</h2>
          <button className="pairing-close" onClick={onClose}>&times;</button>
        </div>

        {uniquePairs.length > 0 && (
          <div className="pairing-section">
            <h3>Paired Couples ({uniquePairs.length})</h3>
            <div className="pair-list">
              {uniquePairs.map(({ a, b }, i) => (
                <div key={i} className="pair-card">
                  <span className="pair-names">
                    {a.displayName || a.firstName} {a.surname}
                    <span className="pair-heart">&hearts;</span>
                    {b ? `${b.displayName || b.firstName} ${b.surname}` : a.partner}
                  </span>
                  <button className="unpair-btn" onClick={() => handleUnpair(a)} title="Unpair">
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="pairing-section">
          <h3>
            Unpaired Guests ({unpaired.length})
            {selected && (
              <span className="pairing-hint">
                Now tap {selected.displayName || selected.firstName}'s partner
              </span>
            )}
          </h3>
          <input
            className="pairing-search"
            type="text"
            placeholder="Search guests..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="unpaired-grid">
            {filtered.map((g) => {
              const isSelected = selected?.firstName === g.firstName && selected?.surname === g.surname;
              return (
                <button
                  key={`${g.firstName}-${g.surname}`}
                  className={`guest-chip ${isSelected ? 'guest-chip-selected' : ''}`}
                  onClick={() => handleSelect(g)}
                >
                  {g.displayName || g.firstName} {g.surname}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestPairingAdmin;
